/**
 * M32 Timeline — GSAP motion from the joselito-timeline prototype. Years ride a continuous wheel
 * (vertical on desktop, horizontal on mobile), the photo swaps with the selected transition variant
 * and the body text slides in line by line. Loops at both ends. One viewport, no scroll lock:
 * drag, year clicks, arrows, keys and horizontal wheel/trackpad change the entry.
 * Tilt alternates direction on consecutive entries (even ← left, odd → right), magnitude 1–5°.
 */
(function () {
  var DESKTOP = '(min-width: 1024px)';
  var ROTATE_MIN = 1;
  var ROTATE_MAX = 5;
  var DRAG_THRESHOLD = 48;
  var WHEEL_THRESHOLD = 40;
  var WHEEL_IDLE = 200;
  var OUT_START = 0.1;
  var TEXT_OUT_DUR = 0.35;
  var DEFAULTS = {
    variant: 'reveal-v',
    invert: false,
    speed: 100,
    photoDelay: 250,
    textDelay: 100,
    year: { ease: 'expo.out', dur: 700, custom: '0.22, 1, 0.36, 1' },
    photo: { ease: 'expo.out', dur: 950, custom: '0.22, 1, 0.36, 1' },
    text: { ease: 'expo.out', dur: 850, custom: '0.22, 1, 0.36, 1' }
  };
  var easeCount = 0;

  function slice(list) {
    return Array.prototype.slice.call(list);
  }

  function wrap(n, count) {
    return ((n % count) + count) % count;
  }

  /* Signed distance on the loop, in [-count / 2, count / 2). */
  function wrapDelta(d, count) {
    return d - count * Math.floor(d / count + 0.5);
  }

  function merge(target, source) {
    Object.keys(source).forEach(function (key) {
      if (!(key in target) || source[key] === null || source[key] === '') return;
      if (typeof target[key] === 'object') merge(target[key], source[key]);
      else target[key] = source[key];
    });
  }

  function readConfig(root) {
    var s = JSON.parse(JSON.stringify(DEFAULTS));
    var el = root.querySelector('[data-timeline-config]');
    try {
      if (el) merge(s, JSON.parse(el.textContent));
    } catch (e) {}
    s.speed = Math.max(0.25, Number(s.speed) / 100 || 1);
    s.photoDelay = Number(s.photoDelay) / 1000;
    s.textDelay = Number(s.textDelay) / 1000;
    ['year', 'photo', 'text'].forEach(function (key) {
      s[key].dur = Number(s[key].dur) / 1000 || DEFAULTS[key].dur / 1000;
    });
    return s;
  }

  function parseBezier(str) {
    var m = String(str)
      .replace(/cubic-bezier|[()]/g, '')
      .split(/[\s,]+/)
      .filter(Boolean)
      .map(Number);
    if (m.length !== 4 || m.some(isNaN) || m[0] < 0 || m[0] > 1 || m[2] < 0 || m[2] > 1) return null;
    return m;
  }

  function easeFor(group) {
    if (group.ease !== 'custom') return group.ease;
    var b = parseBezier(group.custom);
    if (!b || !window.CustomEase) return 'expo.out';
    var name = 'm32-ease-' + ++easeCount;
    window.CustomEase.create(name, 'M0,0 C' + b[0] + ',' + b[1] + ' ' + b[2] + ',' + b[3] + ' 1,1');
    return name;
  }

  /* d = +1 when the new photo arrives "from below" (next), -1 from above. */
  function startFor(variant, p, d) {
    switch (variant) {
      case 'reveal-v':
        return { set: { y: d * 70, rotation: p.rot + d * 4 }, clip: d > 0 ? 'inset(100% 0% 0% 0%)' : 'inset(0% 0% 100% 0%)', img: 1.1 };
      case 'reveal-h':
        return { set: { x: d * 90, rotation: p.rot + d * 2 }, clip: d > 0 ? 'inset(0% 0% 0% 100%)' : 'inset(0% 100% 0% 0%)', img: 1.1 };
      case 'slide':
        return { set: { y: d * 580, rotation: p.rot + d * 6 }, img: 1 };
      case 'drop':
        return { set: { y: -d * 620, rotation: p.rot - d * 12 }, img: 1 };
      case 'zoom':
        return { set: { scale: 1.25, autoAlpha: 0 }, img: 1.15 };
      case 'fan':
        return { set: { scale: 0.94, y: 24, autoAlpha: 0.4 }, img: 1, low: true };
      case 'iris':
        return { set: {}, clip: 'circle(0% at ' + (d > 0 ? 86 : 14) + '% 50%)', img: 1.12 };
      default:
        return { set: { autoAlpha: 0 }, img: 1 };
    }
  }

  function leaveFor(variant, p, d, D, delayIn, selEase, outStart) {
    var out = D * 0.8;
    var e = 'power3.inOut';
    switch (variant) {
      case 'reveal-v':
        return { vars: { y: -d * 80, rotation: p.rot - d * 3, autoAlpha: 0 }, dur: out, delay: outStart, ease: e };
      case 'reveal-h':
        return { vars: { x: -d * 100, autoAlpha: 0 }, dur: out, delay: outStart, ease: e };
      case 'slide':
        return { vars: { y: -d * 580, rotation: p.rot - d * 5 }, dur: D, delay: delayIn, ease: selEase };
      case 'drop':
        return { vars: { scale: 0.94 }, dur: D * 0.6, delay: delayIn + D * 0.35, ease: e };
      case 'zoom':
        return { vars: { scale: 0.92, autoAlpha: 0 }, dur: out, delay: outStart, ease: e };
      case 'fan':
        return { vars: { rotation: p.rot - d * 22, x: -d * 140, y: 60, autoAlpha: 0 }, dur: out, delay: outStart, ease: e };
      case 'iris':
        return { vars: { autoAlpha: 0 }, dur: 0.3, delay: delayIn + D * 0.7, ease: 'none' };
      default:
        return { vars: { autoAlpha: 0 }, dur: out, delay: delayIn, ease: 'none' };
    }
  }

  function initTimeline(root) {
    if (!root || root.dataset.timelineBound === 'true' || !window.gsap) return;

    var gsap = window.gsap;
    var stage = root.querySelector('[data-timeline-stage]');
    var years = slice(root.querySelectorAll('[data-timeline-year]'));
    var mediaItems = slice(root.querySelectorAll('[data-timeline-media]'));
    var panels = slice(root.querySelectorAll('[data-timeline-body]'));
    var prevBtn = root.querySelector('[data-timeline-prev]');
    var nextBtn = root.querySelector('[data-timeline-next]');
    var count = years.length;
    if (!count) return;

    root.dataset.timelineBound = 'true';
    if (window.CustomEase) gsap.registerPlugin(window.CustomEase);

    var S = readConfig(root);
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var ease = { year: easeFor(S.year), photo: easeFor(S.photo), text: easeFor(S.text) };

    function t(seconds) {
      return seconds / S.speed;
    }

    /* Year wheel */
    var state = { pos: 0, hover: years.map(function () { return 0; }) };
    var target = 0;
    var current = 0;
    var sizes = [];
    var step = 0;
    var vertical = false;
    var dim = 0.3;

    function measure() {
      vertical = window.matchMedia(DESKTOP).matches;
      sizes = years.map(function (year) {
        return vertical ? year.offsetHeight : year.offsetWidth;
      });
      step = sizes.reduce(function (sum, size) { return sum + size; }, 0) / count;
      dim = parseFloat(window.getComputedStyle(root).getPropertyValue('--timeline-year-dim')) || 0.3;
    }

    /* Center-to-center distance between year j and j + 1 (box sizes include the gap). */
    function span(j) {
      return (sizes[wrap(j, count)] + sizes[wrap(j + 1, count)]) / 2;
    }

    function offsetFor(d) {
      var base = Math.floor(state.pos);
      var frac = state.pos - base;
      var k = Math.round(d + frac);
      var offset = 0;
      var j;
      if (k > 0) for (j = 0; j < k; j++) offset += span(base + j);
      else for (j = -1; j >= k; j--) offset -= span(base + j);
      return offset - frac * span(base);
    }

    function render() {
      for (var i = 0; i < count; i++) {
        var d = wrapDelta(i - state.pos, count);
        var a = Math.abs(d);
        var rest = Math.min(1, dim * (1 + state.hover[i]));
        var seam = count > 2 ? Math.min(1, Math.max(0, (count / 2 - a) * 2)) : 1;
        var op = (1 - (1 - rest) * Math.min(1, a)) * Math.min(1, Math.max(0, 3 - a)) * seam;
        var year = years[i];
        var offset = op > 0.01 ? offsetFor(d) : d * step;
        year.style.zIndex = a < 0.5 ? 3 : 1;
        year.classList.toggle('is-active', a < 0.5);
        gsap.set(year, {
          x: vertical ? 0 : offset,
          y: vertical ? offset : 0,
          opacity: op,
          visibility: op > 0.01 ? 'inherit' : 'hidden'
        });
      }
    }

    function hover(i, value) {
      var vars = { duration: t(0.3), ease: 'power2.out', onUpdate: render, overwrite: 'auto' };
      vars[i] = value;
      gsap.to(state.hover, vars);
    }

    years.forEach(function (year, i) {
      gsap.set(year, { xPercent: -50, yPercent: -50 });
      year.addEventListener('pointerenter', function (event) {
        if (event.pointerType !== 'touch') hover(i, 1);
      });
      year.addEventListener('pointerleave', function () {
        hover(i, 0);
      });
    });

    /* Photos */
    var zTop = 1;
    var zLow = 0;
    var photos = mediaItems.map(function (item, i) {
      var frame = item.querySelector('[data-timeline-rotate]');
      if (!frame) return null;
      var magnitude = ROTATE_MIN + Math.random() * (ROTATE_MAX - ROTATE_MIN);
      var p = { item: item, el: frame, img: frame.querySelector('img'), rot: i % 2 === 0 ? -magnitude : magnitude };
      gsap.set(item, { zIndex: i === 0 ? 1 : 0 });
      gsap.set(frame, { rotation: p.rot, autoAlpha: i === 0 ? 1 : 0, clipPath: 'inset(0% 0% 0% 0%)' });
      return p;
    });

    function prime(i) {
      [i - 1, i, i + 1].forEach(function (k) {
        var item = mediaItems[wrap(k, count)];
        if (item) item.hidden = false;
      });
    }

    function showPhoto(to, dir) {
      var variant = reduce ? 'fade' : S.variant;
      var d = S.invert ? -dir : dir;
      var D = t(reduce ? 0.3 : S.photo.dur);
      var delayIn = reduce ? 0 : t(S.photoDelay);
      var outStart = reduce ? 0 : t(OUT_START);

      photos.forEach(function (p, k) {
        if (!p) return;
        /* A delayed tween from a previous click would otherwise start later and override this one. */
        gsap.killTweensOf(p.el);
        var visible = gsap.getProperty(p.el, 'opacity') > 0.01;
        if (k === to) {
          var tween = {
            x: 0,
            y: 0,
            rotation: p.rot,
            scale: 1,
            autoAlpha: 1,
            duration: D,
            delay: delayIn,
            ease: ease.photo,
            overwrite: 'auto',
            onComplete: function () {
              gsap.set(p.el, { clipPath: 'inset(0% 0% 0% 0%)' });
            }
          };
          if (!visible) {
            var start = startFor(variant, p, d);
            gsap.set(p.item, { zIndex: start.low ? --zLow : ++zTop });
            gsap.set(p.el, Object.assign({ x: 0, y: 0, scale: 1, rotation: p.rot, autoAlpha: 1 }, start.set));
            gsap.set(p.el, { clipPath: start.clip || 'inset(0% 0% 0% 0%)' });
            if (start.clip) {
              tween.clipPath = start.clip.indexOf('circle') === 0
                ? start.clip.replace('circle(0%', 'circle(150%')
                : 'inset(0% 0% 0% 0%)';
            }
            if (p.img) {
              gsap.set(p.img, { scale: start.img });
              gsap.to(p.img, { scale: 1, duration: D * 1.15, delay: delayIn, ease: 'expo.out', overwrite: 'auto' });
            }
          } else {
            gsap.set(p.item, { zIndex: variant === 'fan' ? --zLow : ++zTop });
            if (String(gsap.getProperty(p.el, 'clipPath')).indexOf('circle') === 0) {
              tween.clipPath = 'circle(150% at ' + (d > 0 ? 86 : 14) + '% 50%)';
            }
            if (p.img) gsap.to(p.img, { scale: 1, duration: D, ease: 'expo.out', overwrite: 'auto' });
          }
          gsap.to(p.el, tween);
        } else if (visible) {
          var leave = leaveFor(variant, p, d, D, delayIn, ease.photo, outStart);
          gsap.to(p.el, Object.assign({
            overwrite: 'auto',
            ease: leave.ease,
            duration: variant === 'iris' ? t(leave.dur) : leave.dur,
            delay: leave.delay,
            onComplete: function () {
              gsap.set(p.el, { autoAlpha: 0 });
            }
          }, leave.vars));
        }
      });
    }

    /* Body text, split into measured lines */
    var shownPanel = panels[0] || null;
    var textTl = null;

    function buildLines(panel) {
      var p = panel && panel.querySelector('[data-timeline-text]');
      if (!p) return [];
      var items = [];
      p.textContent = '';
      p.getAttribute('data-timeline-text').split(/\r?\n/).forEach(function (para, g) {
        if (g) p.appendChild(document.createElement('br'));
        var words = para.split(/\s+/).filter(Boolean);
        if (!words.length) items.push({ g: g, empty: true });
        words.forEach(function (word) {
          var span = document.createElement('span');
          span.textContent = word + ' ';
          p.appendChild(span);
          items.push({ g: g, span: span });
        });
      });

      var lines = [];
      var top = null;
      var group = -1;
      items.forEach(function (item) {
        if (item.empty) {
          lines.push(['\u00a0']);
          group = item.g;
          top = null;
          return;
        }
        var y = item.span.offsetTop;
        if (item.g !== group || top === null || Math.abs(y - top) > 4) {
          lines.push([]);
          top = y;
          group = item.g;
        }
        lines[lines.length - 1].push(item.span.textContent);
      });

      p.textContent = '';
      lines.forEach(function (words) {
        var line = document.createElement('span');
        var inner = document.createElement('span');
        line.className = 'timeline__line';
        inner.textContent = words.join('').replace(/\s+$/, '');
        line.appendChild(inner);
        p.appendChild(line);
      });
      return slice(p.querySelectorAll('.timeline__line > span'));
    }

    function showText(i, dir) {
      if (textTl) textTl.kill();
      var next = panels[i] || null;
      var old = shownPanel ? slice(shownPanel.querySelectorAll('.timeline__line > span')) : [];
      textTl = gsap.timeline({ delay: reduce ? 0 : t(S.textDelay) });
      if (old.length && !reduce) {
        textTl.to(old, { yPercent: -110 * dir, duration: t(TEXT_OUT_DUR), ease: 'power2.in', stagger: t(0.025), overwrite: 'auto' });
      }
      textTl.add(function () {
        panels.forEach(function (panel) {
          var on = panel === next;
          panel.hidden = !on;
          panel.classList.toggle('is-active', on);
        });
        shownPanel = next;
        var lines = buildLines(next);
        if (reduce || !lines.length) return;
        gsap.fromTo(
          lines,
          { yPercent: 110 * dir },
          { yPercent: 0, duration: t(S.text.dur), ease: ease.text, stagger: t(0.07), overwrite: 'auto' }
        );
      });
    }

    /* Navigation (loops) */
    function settle() {
      gsap.to(state, { pos: target, duration: t(reduce ? 0.01 : S.year.dur), ease: ease.year, onUpdate: render, overwrite: 'auto' });
    }

    function goBy(delta) {
      if (count < 2) return;
      if (!delta) {
        settle();
        return;
      }
      var dir = delta > 0 ? 1 : -1;
      var steps = Math.abs(delta);
      target += delta;
      current = wrap(target, count);
      prime(current);
      var dur = reduce ? 0.01 : Math.min(S.year.dur * 1.8, S.year.dur + 0.12 * (steps - 1));
      gsap.to(state, { pos: target, duration: t(dur), ease: ease.year, onUpdate: render, overwrite: 'auto' });
      showPhoto(current, dir);
      showText(current, dir);
    }

    function goTo(i) {
      goBy(wrapDelta(i - current, count));
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        goBy(-1);
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        goBy(1);
      });
    }

    root.addEventListener('keydown', function (event) {
      var k = event.key;
      var isDesktop = window.matchMedia(DESKTOP).matches;
      if (k === 'ArrowRight' || (isDesktop && (k === 'ArrowDown' || k === 'PageDown'))) goBy(1);
      else if (k === 'ArrowLeft' || (isDesktop && (k === 'ArrowUp' || k === 'PageUp'))) goBy(-1);
      else if (k === 'Home') goTo(0);
      else if (k === 'End') goTo(count - 1);
      else return;
      event.preventDefault();
    });

    if (!root.hasAttribute('tabindex')) root.setAttribute('tabindex', '0');

    /* Drag: the wheel follows the pointer, then settles on the nearest year */
    var drag = null;

    if (stage && count > 1) {
      stage.addEventListener('pointerdown', function (event) {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        var year = event.target.closest('[data-timeline-year]');
        gsap.killTweensOf(state);
        drag = {
          id: event.pointerId,
          x: event.clientX,
          y: event.clientY,
          pos: state.pos,
          year: year ? Number(year.dataset.index) : null,
          vertical: window.matchMedia(DESKTOP).matches
        };
        if (event.pointerType !== 'touch') stage.setPointerCapture(event.pointerId);
        stage.classList.add('is-dragging');
      });

      stage.addEventListener('pointermove', function (event) {
        if (!drag || event.pointerId !== drag.id || !step) return;
        var delta = drag.vertical ? event.clientY - drag.y : event.clientX - drag.x;
        state.pos = drag.pos - delta / step;
        render();
      });

      var endDrag = function (event) {
        if (!drag || event.pointerId !== drag.id) return;
        var done = drag;
        var dx = event.clientX - done.x;
        var dy = event.clientY - done.y;
        drag = null;
        stage.classList.remove('is-dragging');
        if (event.type !== 'pointerup') {
          settle();
          return;
        }

        if (Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) {
          if (done.year !== null) goTo(done.year);
          else settle();
          return;
        }

        if (done.vertical && Math.abs(dx) > Math.abs(dy)) {
          goBy(dx < 0 ? 1 : -1);
          return;
        }

        var moved = done.vertical ? dy : dx;
        var nearest = Math.round(state.pos);
        if (nearest === target && Math.abs(moved) >= DRAG_THRESHOLD) nearest = target + (moved < 0 ? 1 : -1);
        goBy(nearest - target);
      };

      stage.addEventListener('pointerup', endDrag);
      stage.addEventListener('pointercancel', endDrag);

      var wheelSum = 0;
      var wheelLocked = false;
      var wheelTimer = null;

      stage.addEventListener(
        'wheel',
        function (event) {
          if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
          event.preventDefault();
          window.clearTimeout(wheelTimer);
          wheelTimer = window.setTimeout(function () {
            wheelSum = 0;
            wheelLocked = false;
          }, WHEEL_IDLE);
          if (wheelLocked) return;
          wheelSum += event.deltaX;
          if (Math.abs(wheelSum) < WHEEL_THRESHOLD) return;
          goBy(wheelSum > 0 ? 1 : -1);
          wheelSum = 0;
          wheelLocked = true;
        },
        { passive: false }
      );
    }

    function layout() {
      measure();
      render();
      if (shownPanel && !(textTl && textTl.isActive())) buildLines(shownPanel);
    }

    var resizeTimer = null;
    window.addEventListener('resize', function () {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(layout, 100);
    });

    measure();
    render();
    prime(0);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(layout);
    else layout();
  }

  function initAll() {
    document.querySelectorAll('[data-timeline]').forEach(initTimeline);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target && event.target.querySelector
      ? event.target.querySelector('[data-timeline]')
      : null;
    if (root) initTimeline(root);
  });
})();
